import { ShieldCheck, Quote } from "lucide-react";

// Each client has a logo image served from /public/clients/.
// Logos cropped from the supplied clientele composite; L&T Construction and JSW Paints
// are clean wordmarks generated for entries the user explicitly requested.
const CLIENTS = [
    { id: "lt", name: "Larsen & Toubro", category: "Engineering", img: "/clients/lt.png" },
    { id: "lt-construction", name: "L&T Construction", category: "Construction", img: "/clients/lt-construction.png" },
    { id: "jsw-paints", name: "JSW Paints", category: "Manufacturing", img: "/clients/jsw-paints.png" },
    { id: "reliance", name: "Reliance Industries", category: "Conglomerate", img: "/clients/reliance.png" },
    { id: "torrent", name: "Torrent Power", category: "Power", img: "/clients/torrent.png" },
    { id: "gandhi", name: "Gandhi Automations", category: "Automation", img: "/clients/gandhi.png" },
    { id: "synergia", name: "Synergia Life Sciences", category: "Pharma", img: "/clients/synergia.png" },
    { id: "shivkrupa", name: "Shivkrupa Steel & Alloys", category: "Steel", img: "/clients/shivkrupa.png" },
    { id: "jay-jagdamba", name: "Jay Jagdamba Ltd.", category: "Steel", img: "/clients/jay-jagdamba.png" },
    { id: "skytech", name: "Skytech Rolling Mill", category: "Steel", img: "/clients/skytech.png" },
    { id: "zeeco", name: "Zeeco", category: "Energy", img: "/clients/zeeco.png" },
    { id: "sukraft", name: "Sukraft", category: "Manufacturing", img: "/clients/sukraft.png" },
    { id: "keller", name: "Keller", category: "Geotechnical", img: "/clients/keller.png" },
];

export const Clientele = () => {
    return (
        <section
            id="clientele"
            data-testid="clientele-section"
            className="relative py-24 lg:py-32 bg-[#0A0A0A] overflow-hidden"
        >
            {/* Decorative bg word */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 font-display text-[22vw] leading-none text-white/[0.025] uppercase whitespace-nowrap select-none"
            >
                Trusted By
            </div>

            <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
                {/* Header */}
                <div className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-10 h-[2px] bg-[#FFC107]" />
                            <span className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                                Clientele
                            </span>
                        </div>
                        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9]">
                            Trusted By
                            <br />
                            <span className="text-neutral-500">
                                India&apos;s Builders.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-md font-body text-neutral-400 text-base lg:text-lg leading-relaxed">
                        From construction giants to power, steel, pharma and
                        chemical plants — we&apos;ve been the lifting partner
                        on hundreds of critical jobs.
                    </p>
                </div>

                {/* Logo grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {CLIENTS.map((c, idx) => (
                        <article
                            key={c.id}
                            data-testid={`client-card-${c.id}`}
                            className="reveal group relative bg-[#0F0F0F] border border-white/10 hover:border-[#FFC107]/50 transition-all duration-300 flex flex-col"
                            style={{ animationDelay: `${(idx % 5) * 0.05}s` }}
                            title={c.name}
                        >
                            {/* Logo plate (white) */}
                            <div className="relative bg-white p-4 sm:p-5 h-24 sm:h-28 flex items-center justify-center overflow-hidden">
                                <img
                                    src={c.img}
                                    alt={`${c.name} logo`}
                                    loading="lazy"
                                    decoding="async"
                                    className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => {
                                        // Hide image and show fallback on error
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                            {/* Caption strip (dark) */}
                            <div className="px-4 py-3 border-t border-white/10">
                                <div className="font-body text-[12px] sm:text-sm text-white leading-tight font-semibold">
                                    {c.name}
                                </div>
                                <div className="font-body text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-1">
                                    {c.category}
                                </div>
                            </div>

                            {/* Yellow corner mark on hover */}
                            <span className="absolute top-0 right-0 w-2 h-2 bg-[#FFC107] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </article>
                    ))}
                </div>

                {/* Footer note */}
                <div className="reveal mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 flex items-start gap-4 bg-[#0F0F0F] border border-white/10 p-6">
                        <Quote
                            size={28}
                            strokeWidth={1.6}
                            className="text-[#FFC107] shrink-0 mt-1"
                        />
                        <p className="font-body text-sm md:text-base text-neutral-300 leading-relaxed">
                            &quot;Surya Crane has been our go-to partner for
                            heavy lifts across plant maintenance and new
                            projects. Punctual, safe and the operators know
                            their machines inside out.&quot;
                            <span className="block mt-3 font-body text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                                — Project Lead, Industrial Client
                            </span>
                        </p>
                    </div>
                    <div className="bg-[#FFC107] text-black p-6 flex flex-col justify-between">
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={22} strokeWidth={2} />
                            <span className="font-body text-xs uppercase tracking-[0.22em] font-bold">
                                Vendor Approved
                            </span>
                        </div>
                        <p className="font-display text-2xl uppercase mt-3 leading-tight">
                            Empanelled with Tier-1 EPC &amp; Industrial
                            Contractors.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};
